import type { Section } from '@common/formEngine/formEngine.types';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { NullablePrimitives } from '@common/utils/utilityTypes';
import { dependsOn } from '@common/formEngine/dependsOn';

export enum AanvalsmedicatieAdvies {
    aanvalsmedicatie_afbouwen = 'Aanvalsmedicatie afbouwen',
    aanvalsmedicatie_stoppen = 'Aanvalsmedicatie stoppen',
    aanvalsmedicatie_voorschrijven = 'Aanvalsmedicatie aanbeveling',
}
export const aanvalsmedicatieAdviesOptions: AanvalsmedicatieAdvies[] = [
    AanvalsmedicatieAdvies.aanvalsmedicatie_voorschrijven,
    AanvalsmedicatieAdvies.aanvalsmedicatie_stoppen,
    AanvalsmedicatieAdvies.aanvalsmedicatie_afbouwen,
];

export enum AanvalsmedicatieVoorschrijven {
    nsaids = `NSAID's (Ibuprofen, Diclofenac, Naproxen)`,
    paracetamol = 'Paracetamol',
    sumatriptan_autoinjector = 'Sumatriptan auto-injector',
    triptanen = 'Triptanen',
}
export const aanvalsmedicatieVoorschrijvenOptions: AanvalsmedicatieVoorschrijven[] = [
    AanvalsmedicatieVoorschrijven.paracetamol,
    AanvalsmedicatieVoorschrijven.nsaids,
    AanvalsmedicatieVoorschrijven.triptanen,
    AanvalsmedicatieVoorschrijven.sumatriptan_autoinjector,
];

export enum Diagnose {
    chronische_migraine = 'Chronische migraine',
    chronische_spanningshoofdpijn = 'Chronische spanningshoofdpijn',
    episodische_migraine = 'Episodische migraine',
    episodische_spanningshoofdpijn = 'Episodische spanningshoofdpijn',
    hoofdpijn_door_overgebruik_van_medicatie = 'Hoofdpijn door overgebruik van medicatie',
    onduidelijk = 'Onduidelijk',
}
export const diagnoseOptions: Diagnose[] = [
    Diagnose.episodische_spanningshoofdpijn,
    Diagnose.chronische_spanningshoofdpijn,
    Diagnose.episodische_migraine,
    Diagnose.chronische_migraine,
    Diagnose.hoofdpijn_door_overgebruik_van_medicatie,
    Diagnose.onduidelijk,
];

export enum Specialist {
    algemene_neuroloog = 'Algemene neuroloog',
    hoofdpijnneuroloog = 'Hoofdpijnneuroloog',
}
export const specialistOptions: Specialist[] = [Specialist.hoofdpijnneuroloog, Specialist.algemene_neuroloog];

export enum PreventieveMedicatieType {
    amitriptyline = 'Amitriptyline',
    candesartan = 'Candesartan',
    gabapentine = 'Gabapentine',
    metoprolol = 'Metoprolol',
    nortriptyline = 'Nortriptyline',
    pregabaline = 'Pregabaline',
    propranolol = 'Propranolol',
    topiramaat = 'Topiramaat',
    verapamil = 'Verapamil (disclaimer: na ECG)',
}
export const preventieveMedicatieTypeOptions: PreventieveMedicatieType[] = [
    PreventieveMedicatieType.candesartan,
    PreventieveMedicatieType.metoprolol,
    PreventieveMedicatieType.propranolol,
    PreventieveMedicatieType.topiramaat,
    PreventieveMedicatieType.verapamil,
    PreventieveMedicatieType.amitriptyline,
    PreventieveMedicatieType.nortriptyline,
    PreventieveMedicatieType.pregabaline,
    PreventieveMedicatieType.gabapentine,
];

export type HeadacheAssessmentData = NullablePrimitives<{
    aanvalsmedicatie_advies: AanvalsmedicatieAdvies;
    aanvalsmedicatie_advies_duur_van_periode_afbouwen: string;
    aanvalsmedicatie_advies_duur_van_periode_stoppen: string;
    aanvalsmedicatie_advies_toevoegen: boolean;
    aanvalsmedicatie_advies_voorschrijven: AanvalsmedicatieVoorschrijven;
    aanvalsmedicatie_opmerking: string;
    conclusie_diagnose: Diagnose;
    conclusie_doorverwijzing: boolean;
    conclusie_doorverwijzing_specialist: Specialist;
    conclusie_toelichting: string;
    huisarts_antwoorden_of_opmerkingen: string;
    niet_medicamenteuze_behandelingen_advies: boolean;
    niet_medicamenteuze_behandelingen_opmerking: string;
    preventieve_medicatie_advies_toevoegen: boolean;
    preventieve_medicatie_advies_type: PreventieveMedicatieType;
    preventieve_medicatie_opmerking: string;
    triggers_en_leefstijl_advies: boolean;
    triggers_en_leefstijl_opmerking: string;
}>;

export type HeadacheAssessmentKey = DeepPickPath<HeadacheAssessmentData>;

export const headacheAssessmentSections: Section<HeadacheAssessmentData>[] = [
    {
        fields: [
            {
                allowOtherOption: true,
                isRequired: true,
                key: 'conclusie_diagnose',
                label: 'Werkdiagnose',
                type: 'radioGroup',
                valueOptions: diagnoseOptions,
            },
            {
                isRequired: true,
                key: 'conclusie_doorverwijzing',
                label: 'Doorverwijzing',
                type: 'boolean',
            },
            {
                include: dependsOn('conclusie_doorverwijzing'),
                isRequired: true,
                key: 'conclusie_doorverwijzing_specialist',
                label: 'Specialist',
                type: 'radioGroup',
                valueOptions: specialistOptions,
            },
            {
                key: 'conclusie_toelichting',
                label: 'Toelichting',
                type: 'textArea',
            },
        ],
        title: 'Conclusie',
    },
    {
        fields: [
            {
                include: dependsOn(
                    'conclusie_doorverwijzing',
                    ({ conclusie_doorverwijzing }) => conclusie_doorverwijzing === false
                ),
                isRequired: true,
                key: 'aanvalsmedicatie_advies_toevoegen',
                label: 'Toevoegen advies aanvalsmedicatie',
                type: 'boolean',
            },
            {
                include: dependsOn('aanvalsmedicatie_advies_toevoegen'),
                isRequired: true,
                key: 'aanvalsmedicatie_advies',
                label: 'Advies aanvalsmedicatie',
                type: 'radioGroup',
                valueOptions: aanvalsmedicatieAdviesOptions,
            },
            {
                include: dependsOn(
                    'aanvalsmedicatie_advies',
                    ({ aanvalsmedicatie_advies }) =>
                        aanvalsmedicatie_advies === AanvalsmedicatieAdvies.aanvalsmedicatie_voorschrijven
                ),
                isRequired: true,
                key: 'aanvalsmedicatie_advies_voorschrijven',
                label: 'Aanvalsmedicatie aanbeveling',
                type: 'radioGroup',
                valueOptions: aanvalsmedicatieVoorschrijvenOptions,
            },
            {
                append: 'weken',
                include: dependsOn(
                    'aanvalsmedicatie_advies',
                    ({ aanvalsmedicatie_advies }) =>
                        aanvalsmedicatie_advies === AanvalsmedicatieAdvies.aanvalsmedicatie_afbouwen
                ),
                isRequired: true,
                key: 'aanvalsmedicatie_advies_duur_van_periode_afbouwen',
                label: 'Duur van periode afbouwen medicatie',
                maxValue: 26,
                minValue: 0,
                type: 'number',
            },
            {
                append: 'weken',
                include: dependsOn(
                    'aanvalsmedicatie_advies',
                    ({ aanvalsmedicatie_advies }) =>
                        aanvalsmedicatie_advies === AanvalsmedicatieAdvies.aanvalsmedicatie_stoppen
                ),
                isRequired: true,
                key: 'aanvalsmedicatie_advies_duur_van_periode_stoppen',
                label: 'Duur van periode stoppen met medicatie',
                maxValue: 26,
                minValue: 0,
                type: 'number',
            },
            {
                include: dependsOn(
                    'conclusie_doorverwijzing',
                    ({ conclusie_doorverwijzing }) => conclusie_doorverwijzing === false
                ),
                key: 'aanvalsmedicatie_opmerking',
                label: 'Opmerking aanvalsmedicatie',
                type: 'textArea',
            },
            {
                include: dependsOn(
                    'conclusie_doorverwijzing',
                    ({ conclusie_doorverwijzing }) => conclusie_doorverwijzing === false
                ),
                isRequired: true,
                key: 'preventieve_medicatie_advies_toevoegen',
                label: 'Toevoegen advies preventieve medicatie',
                type: 'boolean',
            },
            {
                allowOtherOption: true,
                include: dependsOn('preventieve_medicatie_advies_toevoegen'),
                isRequired: true,
                key: 'preventieve_medicatie_advies_type',
                label: 'Type preventieve medicatie',
                type: 'radioGroup',
                valueOptions: preventieveMedicatieTypeOptions,
            },
            {
                include: dependsOn(
                    'conclusie_doorverwijzing',
                    ({ conclusie_doorverwijzing }) => conclusie_doorverwijzing === false
                ),
                key: 'preventieve_medicatie_opmerking',
                label: 'Opmerking preventieve medicatie',
                type: 'textArea',
            },
            {
                include: dependsOn('conclusie_diagnose'),
                isRequired: true,
                key: 'triggers_en_leefstijl_advies',
                label: 'Toevoegen advies triggers en leefstijl',
                type: 'boolean',
            },
            {
                include: dependsOn('conclusie_diagnose'),
                key: 'triggers_en_leefstijl_opmerking',
                label: 'Opmerking triggers en leefstijl',
                type: 'textArea',
            },
            {
                include: dependsOn(
                    'conclusie_diagnose',
                    ({ conclusie_diagnose }) =>
                        conclusie_diagnose === Diagnose.episodische_spanningshoofdpijn ||
                        conclusie_diagnose === Diagnose.chronische_spanningshoofdpijn ||
                        conclusie_diagnose === Diagnose.episodische_migraine ||
                        conclusie_diagnose === Diagnose.chronische_migraine
                ),
                isRequired: true,
                key: 'niet_medicamenteuze_behandelingen_advies',
                label: 'Toevoegen advies niet-medicamenteuze behandelingen',
                type: 'boolean',
            },
            {
                include: dependsOn(
                    'conclusie_diagnose',
                    ({ conclusie_diagnose }) =>
                        conclusie_diagnose === Diagnose.episodische_spanningshoofdpijn ||
                        conclusie_diagnose === Diagnose.chronische_spanningshoofdpijn ||
                        conclusie_diagnose === Diagnose.episodische_migraine ||
                        conclusie_diagnose === Diagnose.chronische_migraine
                ),
                key: 'niet_medicamenteuze_behandelingen_opmerking',
                label: 'Opmerking niet-medicamenteuze behandelingen',
                type: 'textArea',
            },
        ],
        title: 'Advies en behandelbeleid',
    },
    {
        fields: [
            {
                key: 'huisarts_antwoorden_of_opmerkingen',
                label: 'Antwoorden of opmerkingen',
                type: 'textArea',
            },
        ],
        title: 'Bericht aan huisarts',
    },
];
